import { Trans } from '@lingui/react/macro';
import { motion } from 'framer-motion';

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '~/components/ui/accordion';

export default function FAQSection() {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-10 text-brand-dark-blue">
            <Trans>Frequently Asked Questions</Trans>
          </h2>

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="how-it-works">
              <AccordionTrigger className="text-left text-base font-semibold">
                <Trans>How does a mock interview session work?</Trans>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed space-y-2">
                <p>
                  <Trans>
                    You define the role you're applying for, select targeted questions from our
                    curated bank, and begin. Pavone records your answers question by question.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    When the session ends, your performance is analyzed and you receive a structured,
                    detailed report with actionable improvement insights.
                  </Trans>
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="what-analyzed">
              <AccordionTrigger className="text-left text-base font-semibold">
                <Trans>What exactly does Pavone analyze?</Trans>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed space-y-2">
                <p>
                  <Trans>
                    Pavone evaluates your clarity, structure, pacing, filler words, answer depth, and
                    confidence signals.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    You don't just get metrics — you get precise guidance on what to fix and how to
                    improve before your real interview.
                  </Trans>
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="different-from-alone">
              <AccordionTrigger className="text-left text-base font-semibold">
                <Trans>How is this different from practicing on my own?</Trans>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed space-y-2">
                <p>
                  <Trans>
                    Practicing alone builds familiarity. Pavone builds performance.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    It identifies blind spots you cannot reliably detect yourself — weak structure,
                    overused fillers, rushed delivery, unclear positioning — and turns them into
                    measurable improvements.
                  </Trans>
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="get-started">
              <AccordionTrigger className="text-left text-base font-semibold">
                <Trans>What do I need to get started?</Trans>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed space-y-2">
                <p>
                  <Trans>
                    A laptop or phone with a microphone. No downloads. No setup.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    Start your session directly in your browser.
                  </Trans>
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="privacy">
              <AccordionTrigger className="text-left text-base font-semibold">
                <Trans>Are my recordings private?</Trans>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed space-y-2">
                <p>
                  <Trans>
                    Yes. Your recordings are securely processed to generate your personal report.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    They are not shared, published, or used for any purpose beyond your session
                    analysis.
                  </Trans>
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="how-many">
              <AccordionTrigger className="text-left text-base font-semibold">
                <Trans>How many interviews can I practice?</Trans>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed space-y-2">
                <p>
                  <Trans>
                    You can run multiple sessions based on your plan.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    Practice different roles. Refine specific answers. Improve progressively. Track
                    your development over time.
                  </Trans>
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="who-for">
              <AccordionTrigger className="text-left text-base font-semibold">
                <Trans>Who is Pavone designed for?</Trans>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed space-y-2">
                <p>
                  <Trans>
                    Pavone is built for candidates who take preparation seriously — from first-time
                    applicants to professionals pursuing competitive roles.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    If the outcome matters, preparation should be deliberate.
                  </Trans>
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <p className="text-center text-gray-500 mt-8 text-sm">
            <Trans>Haven't found what you are looking for?</Trans>{' '}
            <a href="mailto:info@pavone.ai" className="text-brand-dark-blue hover:underline font-medium">
              <Trans>Send us an email</Trans>
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
